import { N, round, ceil } from './helpers.mjs';

export const CARPENTERS = [
  // ====================== CARPENTERS ======================
  {
    slug: 'stud-wall',
    trade: 'carpenters',
    name: 'Stud Wall Timber',
    blurb: 'Studs @400mm centres + head/sole plates + noggins.',
    inputs: [
      { key: 'length', label: 'Wall length', unit: 'm', placeholder: '4' },
      { key: 'height', label: 'Wall height', unit: 'm', placeholder: '2.4' },
      { key: 'spacing', label: 'Stud spacing', unit: 'mm', placeholder: '400', default: 400 },
    ],
    compute: (v) => {
      const sp = (N(v.spacing) || 400) / 1000;
      const studs = ceil(N(v.length) / sp) + 1;
      const studLen = studs * N(v.height);
      const plates = N(v.length) * 2;
      const noggins = ceil((studs - 1) * sp);
      const total = round(studLen + plates + noggins, 1);
      return [
        { label: 'Studs', value: studs, unit: 'pcs' },
        { label: 'Plates (head + sole)', value: round(plates, 1), unit: 'm' },
        { label: 'Noggins', value: noggins, unit: 'm' },
        { label: 'Total CLS timber', value: total, unit: 'm' },
      ];
    },
  },
  {
    slug: 'floor-joists',
    trade: 'carpenters',
    name: 'Floor Joists',
    blurb: 'Joists @400mm centres.',
    inputs: [
      { key: 'width', label: 'Room width (perp. to joists)', unit: 'm', placeholder: '4' },
      { key: 'length', label: 'Joist span', unit: 'm', placeholder: '3.5' },
      { key: 'spacing', label: 'Spacing', unit: 'mm', placeholder: '400', default: 400 },
    ],
    compute: (v) => {
      const sp = (N(v.spacing) || 400) / 1000;
      const joists = ceil(N(v.width) / sp) + 1;
      return [
        { label: 'Joists needed', value: joists, unit: 'pcs' },
        { label: 'Total timber length', value: round(joists * N(v.length), 1), unit: 'm' },
      ];
    },
  },

  {
    slug: 'stair-stringer',
    trade: 'carpenters',
    name: 'Stair Stringer',
    blurb: 'Work out risers, treads, stringer length and pitch from a total floor-to-floor rise. Defaults to UK Part K domestic limits (rise ≤ 220mm, pitch ≤ 42°).',
    inputs: [
      { key: 'totalRise', label: 'Total rise (floor to floor)', unit: 'mm', placeholder: '2600' },
      { key: 'targetRise', label: 'Target rise per step', unit: 'mm', placeholder: '190', default: 190 },
      { key: 'going', label: 'Going (tread depth)', unit: 'mm', placeholder: '250', default: 250 },
    ],
    compute: (v) => {
      const totalRise = N(v.totalRise);
      const going = N(v.going) || 250;
      const target = N(v.targetRise) || 190;
      if (totalRise <= 0) return [{ label: 'Enter the total rise', value: 0, unit: '' }];
      const risers = Math.max(1, Math.round(totalRise / target));
      const actualRise = totalRise / risers;
      const treads = risers - 1;
      const totalGoing = treads * going;
      const stringerLen = Math.sqrt(totalRise * totalRise + totalGoing * totalGoing);
      const angle = Math.atan(actualRise / going) * 180 / Math.PI;
      return [
        { label: 'Number of risers', value: risers, unit: '' },
        { label: 'Number of treads', value: treads, unit: '' },
        { label: 'Actual rise per step', value: round(actualRise, 1), unit: 'mm' },
        { label: 'Total going (horizontal run)', value: round(totalGoing, 0), unit: 'mm' },
        { label: 'Stringer length (min)', value: round(stringerLen, 0), unit: 'mm' },
        { label: 'Stair pitch', value: round(angle, 1), unit: '°' },
        { label: '2×rise + going (550–700 ideal)', value: round(2 * actualRise + going, 0), unit: 'mm' },
      ];
    },
  },
  {
    slug: 'timber-quantity',
    trade: 'carpenters',
    name: 'Timber Quantity',
    blurb: 'Number of lengths to buy for framing, battens, CLS or treated timber from the total run you need, including waste and timber volume.',
    inputs: [
      { key: 'totalLength', label: 'Total length required', unit: 'm', placeholder: '60' },
      {
        key: 'type',
        label: 'Timber type',
        type: 'select',
        default: 'cls-63',
        options: [
          { value: 'batten-50', label: 'Batten 25 × 50mm' },
          { value: 'cls-63', label: 'CLS / framing 38 × 63mm' },
          { value: 'cls-89', label: 'CLS 38 × 89mm' },
          { value: 'treated-100', label: 'Treated 47 × 100mm' },
          { value: 'treated-150', label: 'Treated 47 × 150mm' },
        ],
      },
      {
        key: 'stock',
        label: 'Stock length',
        type: 'select',
        default: '4.8',
        options: [
          { value: '2.4', label: '2.4m' }, { value: '3.0', label: '3.0m' },
          { value: '3.6', label: '3.6m' }, { value: '4.2', label: '4.2m' },
          { value: '4.8', label: '4.8m' }, { value: '6.0', label: '6.0m' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const SECTION = { 'batten-50': 25 * 50, 'cls-63': 38 * 63, 'cls-89': 38 * 89, 'treated-100': 47 * 100, 'treated-150': 47 * 150 };
      const lm = N(v.totalLength) * (1 + N(v.wastage) / 100);
      const stock = parseFloat(v.stock) || 4.8;
      const pieces = ceil(lm / stock);
      const areaMm2 = SECTION[v.type] || SECTION['cls-63'];
      return [
        { label: 'Linear metres (incl. wastage)', value: round(lm, 1), unit: 'm' },
        { label: `Lengths to buy (${stock}m)`, value: pieces, unit: 'pcs' },
        { label: 'Total purchased length', value: round(pieces * stock, 1), unit: 'm' },
        { label: 'Timber volume', value: round(pieces * stock * areaMm2 * 1e-6, 3), unit: 'm³' },
      ];
    },
  },
  {
    slug: 'sheet-material',
    trade: 'carpenters',
    name: 'Sheet Material',
    blurb: 'Sheets of plywood, OSB, MDF or chipboard needed to cover an area, with a waste allowance.',
    inputs: [
      { key: 'area', label: 'Area to cover', unit: 'm²', placeholder: '30' },
      {
        key: 'material',
        label: 'Material',
        type: 'select',
        default: 'ply',
        options: [
          { value: 'ply', label: 'Plywood' }, { value: 'osb', label: 'OSB' },
          { value: 'mdf', label: 'MDF' }, { value: 'chipboard', label: 'Chipboard' },
        ],
      },
      {
        key: 'sheet',
        label: 'Sheet size',
        type: 'select',
        default: '2440x1220',
        options: [
          { value: '2440x1220', label: '2440 × 1220mm (8×4 — 2.98 m²)' },
          { value: '2400x1200', label: '2400 × 1200mm (2.88 m²)' },
          { value: '1220x2440', label: '1220 × 2440mm flooring (2.98 m²)' },
          { value: '600x2400', label: '600 × 2400mm T&G flooring (1.44 m²)' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const SHEET = { '2440x1220': 2.9768, '2400x1200': 2.88, '1220x2440': 2.9768, '600x2400': 1.44 };
      const area = N(v.area);
      const sheetArea = SHEET[v.sheet] || 2.9768;
      if (area <= 0) return [{ label: 'Enter an area', value: 0, unit: '' }];
      const noWaste = ceil(area / sheetArea);
      const withWaste = ceil(area * (1 + N(v.wastage) / 100) / sheetArea);
      return [
        { label: 'Sheet coverage', value: round(sheetArea, 2), unit: 'm²' },
        { label: 'Sheets (no wastage)', value: noWaste, unit: 'sheets' },
        { label: 'Sheets (incl. wastage)', value: withWaste, unit: 'sheets' },
        { label: 'Total coverage', value: round(withWaste * sheetArea, 1), unit: 'm²' },
      ];
    },
  },
  {
    slug: 'noggin',
    trade: 'carpenters',
    name: 'Noggins / Blocking',
    blurb: 'Number of noggins between studs or joists and the timber length required, for one or more rows.',
    inputs: [
      { key: 'length', label: 'Wall / floor length', unit: 'm', placeholder: '4' },
      { key: 'centres', label: 'Stud / joist centres', unit: 'mm', placeholder: '400', default: 400 },
      { key: 'memberWidth', label: 'Stud / joist width', unit: 'mm', placeholder: '45', default: 45 },
      {
        key: 'rows',
        label: 'Rows of noggins',
        type: 'select',
        default: '1',
        options: [{ value: '1', label: '1 row' }, { value: '2', label: '2 rows' }, { value: '3', label: '3 rows' }],
      },
    ],
    compute: (v) => {
      const centres = (N(v.centres) || 400) / 1000;
      const len = N(v.length);
      if (len <= 0) return [{ label: 'Enter a length', value: 0, unit: '' }];
      const bays = ceil(len / centres); // gaps between members
      const rows = parseInt(v.rows) || 1;
      const totalNoggins = bays * rows;
      const nogginLen = Math.max(0, (N(v.centres) - N(v.memberWidth))) / 1000;
      const timber = totalNoggins * nogginLen * 1.05;
      return [
        { label: 'Noggins per row', value: bays, unit: 'pcs' },
        { label: 'Total noggins', value: totalNoggins, unit: 'pcs' },
        { label: 'Length of each noggin', value: round(nogginLen * 1000, 0), unit: 'mm' },
        { label: 'Timber length (incl. 5%)', value: round(timber, 1), unit: 'm' },
      ];
    },
  },
  {
    slug: 'cutting-list',
    trade: 'carpenters',
    name: 'Cutting List Optimiser',
    blurb: 'Enter your required cuts and a stock length — get the minimum number of lengths to buy and the offcut waste. Format: length × quantity, e.g. "1800x2, 1200x4, 600x6".',
    inputs: [
      { key: 'cuts', label: 'Required cuts (mm × qty)', type: 'textarea', full: true, placeholder: '1800x2, 1200x4, 600x6' },
      { key: 'stock', label: 'Stock length', unit: 'mm', placeholder: '4800', default: 4800 },
      { key: 'kerf', label: 'Saw kerf (blade width)', unit: 'mm', placeholder: '3', default: 3 },
    ],
    compute: (v) => {
      const stock = N(v.stock);
      const kerf = N(v.kerf) || 0;
      const raw = (v.cuts || '').toString();
      if (!raw.trim()) return [{ label: 'Enter your cuts above', value: 0, unit: '' }];
      if (stock <= 0) return [{ label: 'Enter a stock length', value: 0, unit: '' }];
      // Parse: tokens separated by comma/newline; "LxN", "L*N", "L x N" or just "L"
      const pieces = [];
      raw.split(/[,\n;]+/).forEach((tok) => {
        const m = tok.trim().match(/^(\d+(?:\.\d+)?)\s*(?:[x*×]\s*(\d+))?$/i);
        if (m) {
          const L = parseFloat(m[1]);
          const qty = m[2] ? parseInt(m[2]) : 1;
          for (let i = 0; i < qty; i++) if (L > 0) pieces.push(L);
        }
      });
      if (!pieces.length) return [{ label: 'Could not read any cuts — check format', value: 0, unit: '' }];
      const tooLong = pieces.filter((p) => p > stock).length;
      const fit = pieces.filter((p) => p <= stock).sort((a, b) => b - a);
      // First-fit-decreasing bin packing
      const bins = []; // each = remaining length
      fit.forEach((p) => {
        let placed = false;
        for (let i = 0; i < bins.length; i++) {
          const need = p + (bins[i].used > 0 ? kerf : 0);
          if (bins[i].rem >= need) { bins[i].rem -= need; bins[i].used += 1; placed = true; break; }
        }
        if (!placed) bins.push({ rem: stock - p, used: 1 });
      });
      const lengthsNeeded = bins.length;
      const totalCut = fit.reduce((s, p) => s + p, 0);
      const totalStock = lengthsNeeded * stock;
      const waste = totalStock - totalCut;
      const wastePct = totalStock > 0 ? (waste / totalStock) * 100 : 0;
      const out = [
        { label: `Stock lengths to buy (${stock}mm)`, value: lengthsNeeded, unit: 'pcs' },
        { label: 'Pieces cut', value: fit.length, unit: 'cuts' },
        { label: 'Total cut length', value: round(totalCut / 1000, 2), unit: 'm' },
        { label: 'Offcut waste', value: round(waste / 1000, 2), unit: 'm' },
        { label: 'Waste', value: round(wastePct, 1), unit: '%' },
      ];
      if (tooLong > 0) out.push({ label: '⚠ Cuts longer than stock (ignored)', value: tooLong, unit: '' });
      return out;
    },
  },
  {
    slug: 'ceiling-joists',
    trade: 'carpenters',
    name: 'Ceiling Joists',
    blurb: 'Number of ceiling joists at centres and the total timber length. Joists run across the span; spacing is set along the room length.',
    inputs: [
      { key: 'length', label: 'Room length (joists spaced along)', unit: 'm', placeholder: '5' },
      { key: 'span', label: 'Joist span (length of each joist)', unit: 'm', placeholder: '3.6' },
      { key: 'centres', label: 'Joist centres', unit: 'mm', placeholder: '400', default: 400 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const centres = (N(v.centres) || 400) / 1000;
      const len = N(v.length);
      if (len <= 0) return [{ label: 'Enter the room length', value: 0, unit: '' }];
      const joists = ceil(len / centres) + 1;
      const total = joists * N(v.span) * (1 + N(v.wastage) / 100);
      return [
        { label: 'Joists required', value: joists, unit: 'pcs' },
        { label: 'Total timber length', value: round(total, 1), unit: 'm' },
      ];
    },
  },
  {
    slug: 'insulation',
    trade: 'carpenters',
    name: 'Insulation (Stud Walls & Floors)',
    blurb: 'Rolls or boards of insulation needed to fill stud walls, ceilings or floors, with a waste allowance.',
    inputs: [
      { key: 'area', label: 'Area to insulate', unit: 'm²', placeholder: '20' },
      {
        key: 'product',
        label: 'Insulation product',
        type: 'select',
        default: 'roll-100',
        options: [
          { value: 'roll-100', label: '100mm mineral wool roll (5.4 m²/roll)' },
          { value: 'roll-140', label: '140mm mineral wool roll (3.9 m²/roll)' },
          { value: 'roll-170', label: '170mm mineral wool roll (3.05 m²/roll)' },
          { value: 'roll-200', label: '200mm mineral wool roll (2.6 m²/roll)' },
          { value: 'slab-rwa45', label: 'Acoustic slab pack — RWA45 (5.45 m²/pack)' },
          { value: 'pir-board', label: 'PIR board 1200×2400 (2.88 m²/board)' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const COV = { 'roll-100': 5.4, 'roll-140': 3.9, 'roll-170': 3.05, 'roll-200': 2.6, 'slab-rwa45': 5.45, 'pir-board': 2.88 };
      const cov = COV[v.product] || 5.4;
      const area = N(v.area);
      if (area <= 0) return [{ label: 'Enter an area', value: 0, unit: '' }];
      const withWaste = area * (1 + N(v.wastage) / 100);
      const isBoard = v.product === 'pir-board';
      const unit = isBoard ? 'boards' : (v.product.startsWith('slab') ? 'packs' : 'rolls');
      return [
        { label: 'Area (incl. wastage)', value: round(withWaste, 1), unit: 'm²' },
        { label: 'Coverage each', value: round(cov, 2), unit: 'm²' },
        { label: `${unit[0].toUpperCase() + unit.slice(1)} required`, value: ceil(withWaste / cov), unit },
      ];
    },
  },

];
